/**
 * Frontend service for the Google Shared Calendar integration.
 *
 * Wraps the Firebase callable functions defined in
 * functions/src/http/googleCalendar.ts
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GoogleCalendarEvent {
  id: string;
  title: string;
  start: string;           // ISO date-time
  end: string;
  location?: string;
  description?: string;
  isAllDay: boolean;
  htmlLink?: string;
  calendarName?: string;
  calendarColor?: string;
  calendarId?: string;
  rawEventId?: string;
  accountEmail?: string;   // which connected Google account owns this event
  organizerSelf?: boolean;
  organizerEmail?: string;
  colorId?: string;
  status?: string;         // 'confirmed' | 'tentative' | 'cancelled'
  transparency?: string;   // 'opaque' (busy) | 'transparent' (free)
  visibility?: string;     // 'default' | 'public' | 'private' | 'confidential'
  attendees?: Array<{ email: string; displayName?: string; responseStatus?: string; self?: boolean; optional?: boolean }>;
  guestsCanModify?: boolean;
  guestsCanInviteOthers?: boolean;
  guestsCanSeeOtherGuests?: boolean;
  reminders?: { useDefault: boolean; overrides?: Array<{ method: string; minutes: number }> };
  recurrence?: string[];
}

export interface CalendarListEntry {
  id: string;
  name: string;
  color?: string;
  accessRole?: string;
}

export interface ConnectedAccount {
  email: string;
  connectedAt?: string | null;
  tokenExpired?: boolean;
}

export interface GoogleCalendarStatus {
  connected: boolean;
  accounts: ConnectedAccount[];
}

export interface GoogleCalendarEventsResult {
  connected: boolean;
  events: GoogleCalendarEvent[];
  tokenRevoked?: boolean;
}

// ─── Redirect URI ─────────────────────────────────────────────────────────────

export const GOOGLE_CALENDAR_REDIRECT_URI = `${window.location.origin}/google-calendar-callback`;

// ─── Service ──────────────────────────────────────────────────────────────────

class GoogleCalendarService {
  private functions = getFunctions();

  /** Returns the Google OAuth URL the user should be redirected to. */
  async getOAuthUrl(): Promise<string> {
    const fn = httpsCallable<{ redirectUri: string }, { url: string }>(
      this.functions,
      'googleCalendarOAuthUrl'
    );
    const result = await fn({ redirectUri: GOOGLE_CALENDAR_REDIRECT_URI });
    return result.data.url;
  }

  /** Exchanges the authorization code for tokens. */
  async handleOAuthCallback(code: string): Promise<void> {
    const fn = httpsCallable<{ code: string; redirectUri: string }, { success: boolean }>(
      this.functions,
      'googleCalendarOAuthCallback'
    );
    await fn({ code, redirectUri: GOOGLE_CALENDAR_REDIRECT_URI });
  }

  /** Fetches events from all connected Google accounts for the given date range. */
  async getEvents(start: Date, end: Date): Promise<GoogleCalendarEventsResult> {
    const fn = httpsCallable<
      { startIso: string; endIso: string; timeZone: string },
      GoogleCalendarEventsResult
    >(this.functions, 'getGoogleCalendarEvents');

    const result = await fn({
      startIso: start.toISOString(),
      endIso: end.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return result.data;
  }

  /** Disconnects a specific Google account. */
  async disconnect(accountEmail: string): Promise<void> {
    const fn = httpsCallable<{ accountEmail: string }, { success: boolean }>(
      this.functions,
      'disconnectGoogleCalendar'
    );
    await fn({ accountEmail });
  }

  /** Updates a Google Calendar event in-place. */
  async updateEvent(params: {
    calendarId: string;
    rawEventId: string;
    title: string;
    start?: string;
    end?: string;
    timeZone?: string;
    accountEmail: string;
    isAllDay?: boolean;
    location?: string;
    description?: string;
    colorId?: string;
    transparency?: string;
    visibility?: string;
    attendees?: Array<{ email: string; optional?: boolean; responseStatus?: string }>;
    guestsCanModify?: boolean;
    guestsCanInviteOthers?: boolean;
    guestsCanSeeOtherGuests?: boolean;
    reminderMinutes?: number;
    reminderMethod?: string;
    recurrence?: string[];
    newCalendarId?: string;
    organizerEmail?: string;
  }): Promise<{ title: string; start: string; end: string; location?: string; description?: string; isAllDay?: boolean; colorId?: string; transparency?: string; visibility?: string; calendarId?: string; rawEventId?: string }> {
    const fn = httpsCallable<typeof params, { success: boolean; event: { title: string; start: string; end: string; location?: string; description?: string; isAllDay?: boolean; colorId?: string; transparency?: string; visibility?: string; calendarId?: string; rawEventId?: string } }>(
      this.functions,
      'updateGoogleCalendarEvent'
    );
    const result = await fn(params);
    return result.data.event;
  }

  /** Deletes a Google Calendar event. */
  async deleteEvent(calendarId: string, rawEventId: string, accountEmail: string): Promise<void> {
    const fn = httpsCallable<{ calendarId: string; rawEventId: string; accountEmail: string }, { success: boolean }>(
      this.functions,
      'deleteGoogleCalendarEvent'
    );
    await fn({ calendarId, rawEventId, accountEmail });
  }

  /** Returns the list of calendars for a specific connected account. */
  async getCalendarList(accountEmail: string): Promise<CalendarListEntry[]> {
    const fn = httpsCallable<{ accountEmail: string }, { calendars: CalendarListEntry[] }>(
      this.functions,
      'getGoogleCalendarList'
    );
    const result = await fn({ accountEmail });
    return result.data.calendars;
  }

  /** Returns all connected Google accounts and their status. */
  async getStatus(): Promise<GoogleCalendarStatus> {
    const fn = httpsCallable<void, GoogleCalendarStatus>(
      this.functions,
      'getGoogleCalendarStatus'
    );
    const result = await fn();
    return result.data;
  }
}

export const googleCalendarService = new GoogleCalendarService();
